import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightbox — simple full-screen photo viewer.
 *
 *   <Lightbox
 *     open={open}
 *     images={[...]}
 *     index={index}
 *     title="Creekside Cottage"
 *     onClose={() => setOpen(false)}
 *     onIndexChange={setIndex}
 *   />
 */
export default function Lightbox({ open, images = [], index = 0, title, onClose, onIndexChange }) {
  const total = images.length;

  const next = useCallback(() => {
    onIndexChange?.((index + 1) % total);
  }, [index, total, onIndexChange]);

  const prev = useCallback(() => {
    onIndexChange?.((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, next, prev, onClose]);

  if (!open || total === 0) return null;
  const current = images[index];

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-ink/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-5 lg:p-7 text-cream/85 z-10">
        <div className="text-sm tracking-wide">
          {title && <span className="text-cream/85 font-serif text-lg mr-3">{title}</span>}
          <span className="text-cream/55">{index + 1} / {total}</span>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose?.(); }}
          aria-label="Close gallery"
          data-testid="lightbox-close"
          className="text-cream/85 hover:text-cream transition p-2"
        >
          <X size={26} strokeWidth={1.4} />
        </button>
      </div>

      {/* Prev / Next */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous photo"
            data-testid="lightbox-prev"
            className="absolute left-3 lg:left-7 top-1/2 -translate-y-1/2 text-cream/70 hover:text-cream transition p-3 bg-ink/40 hover:bg-ink/70 rounded-full"
          >
            <ChevronLeft size={28} strokeWidth={1.4} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next photo"
            data-testid="lightbox-next"
            className="absolute right-3 lg:right-7 top-1/2 -translate-y-1/2 text-cream/70 hover:text-cream transition p-3 bg-ink/40 hover:bg-ink/70 rounded-full"
          >
            <ChevronRight size={28} strokeWidth={1.4} />
          </button>
        </>
      )}

      {/* Image */}
      <img
        src={current}
        alt={`${title || "Gallery"} — ${index + 1} of ${total}`}
        className="max-w-[92vw] max-h-[85vh] object-contain"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
    </div>
  );
}
